// Hyperliquid-Page — Top-Level Tab unter EVE-Modi.
// Source-of-truth: mockups/06-hyperliquid/proposal.html (v2 mit 21st.dev-Upgrades).
//
// 4 Sub-Tabs (URL-State via useState statt nested router):
//   Markets · Positionen · Leaderboard · Wallet Tracker
//
// Alle Live-Daten verkabelt (Phase 4F + 4G + 4H):
//   - WS-Mids + AssetCtx im AppShell → hier konsumieren wir den Store direkt
//   - REST-Polls (Metrics/OrderBook/Trades/Funding/Predictions) pro Tab
//   - Watched-Wallets aus useWalletMonitor (60s-Poll + D1)
//   - Per-Wallet Stats + Positions via fetchClearinghouseState on-select
//   - Leaderboard via useHlLeaderboard (5min poll stats-data.hyperliquid.xyz)
//   - Positionen-Tab: Top-N-Wallet-Scan via useHlPositions (manual trigger)

import { useEffect, useMemo, useState } from 'react';
import { BarChart3, Eye, ListChecks, Trophy } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useHlLeaderboard } from '@/hooks/use-hl-leaderboard';
import { useHlMetrics } from '@/hooks/use-hl-metrics';
import { useHlPositions } from '@/hooks/use-hl-positions';
import { useWalletAlarms } from '@/hooks/use-wallet-alarms';
import { useWalletMonitor } from '@/hooks/use-wallet-monitor';
import { eveScore } from '@/lib/hl-info-api';
import {
  fetchClearinghouseState,
  type AddWalletInput,
  type UpdateWalletInput,
} from '@/lib/wallet-api';
import { parseOpenPositions } from '@/lib/wallet-monitor';
import { getQuality } from '@/lib/hl-quality';
import {
  selectIsConnected,
  selectLiveMetrics,
  selectLivePrices,
  useHyperliquidStore,
} from '@/stores/hyperliquid-store';
import type { HLPosition } from '@/types/wallet-monitor';
import type { WatchedWallet } from '@/types/wallet-monitor';
import {
  HyperliquidMetricsBar,
  type HyperliquidCoin,
} from '@/components/hyperliquid/hyperliquid-metrics-bar';
import {
  LeaderboardTab,
  type LeaderboardRow as LeaderboardUiRow,
} from '@/components/hyperliquid/leaderboard-tab';
import { MarketsTab } from '@/components/hyperliquid/markets-tab';
import { PositionsTab } from '@/components/hyperliquid/positions-tab';
import {
  WalletTrackerTab,
  type WalletAccountStats,
  type WalletPositionRow,
  type WatchedWalletSummary,
} from '@/components/hyperliquid/wallet-tracker-tab';

type HlSubTab = 'markets' | 'positions' | 'leaderboard' | 'wallets';

interface SubTabSpec {
  id: HlSubTab;
  label: string;
  icon: LucideIcon;
  badge?: number;
}

export function HyperliquidPage() {
  const [coin, setCoin] = useState<HyperliquidCoin>('BTC');
  const [activeTab, setActiveTab] = useState<HlSubTab>('markets');

  // Live-Daten aus dem zentralen HL-Store (WS verbunden im AppShell).
  const liveMetrics = useHyperliquidStore(selectLiveMetrics);
  const livePrices = useHyperliquidStore(selectLivePrices);
  const isConnected = useHyperliquidStore(selectIsConnected);

  // REST-Poll für alle 12 Coins (WS subscribt nur 5) + Vol24h aus dayNtlVlm
  // das im WS-activeAssetCtx fehlt.
  const { metrics: restMetrics, volume24hUsd } = useHlMetrics();

  // WS-Wert bevorzugen (latency 1s), REST als Fallback (alle 30s).
  const wsCtx = liveMetrics[coin];
  const restCtx = restMetrics.get(coin);
  const currentMetrics = wsCtx ?? (restCtx
    ? {
        coin: restCtx.coin,
        funding: restCtx.funding,
        openInterest: restCtx.openInterest,
        markPx: restCtx.markPx,
        oraclePx: restCtx.oraclePx,
        premium: restCtx.premium,
        change: 0,
      }
    : null);
  const currentVolume = volume24hUsd(coin);

  // Watched-Wallets aus DB (60s-Poll).
  const { wallets, addWallet, removeWallet, updateWallet } = useWalletMonitor();

  // Per-Wallet-Threshold Alarms — feuert Browser-Notification wenn eine
  // GETRACKTE Wallet eine Position ≥ ihrer threshold_usd öffnet.
  const walletAlarms = useWalletAlarms({ wallets });

  // Leaderboard für Leaderboard-Tab + als Quelle für Positions-Tab Top-Scan.
  const { rows: lbRawRows } = useHlLeaderboard();
  const leaderboardRows = useMemo<LeaderboardUiRow[]>(
    () =>
      lbRawRows.map((r, idx) => {
        const perf = r.perf.allTime;
        return {
          id: r.ethAddress,
          rank: idx + 1,
          label: r.displayName,
          address: r.ethAddress,
          accountValueUsd: r.accountValue,
          pnlUsd: perf.pnl,
          roiPct: perf.roi * 100,
          volumeUsd: perf.vlm,
          quality: getQuality({
            av: r.accountValue,
            vol: perf.vlm,
            pnl: perf.pnl,
          }),
          avatarInitials: deriveInitials(r.displayName, r.ethAddress),
          explorerUrl: `https://hypurrscan.io/address/${r.ethAddress}`,
        };
      }),
    [lbRawRows],
  );

  // Positions-Tab: Top-Wallet-Scan über clearinghouseState. Manual trigger
  // via Scan-Button — Auto-Scan würde bei jedem Coin-Wechsel 50 HTTP-Calls
  // feuern (Rate-Limit-Risiko).
  const {
    rows: positionRows,
    loading: positionsLoading,
    scanned: positionsScanned,
    total: positionsTotal,
    triggerScan: triggerPositionScan,
  } = useHlPositions(coin, lbRawRows, livePrices, { topN: 50 });

  const positionsScanSubtitle =
    positionsTotal > 0 && positionsLoading
      ? `Scanned ${positionsScanned}/${positionsTotal} Top-Wallets…`
      : positionRows.length > 0
        ? `Top-${positionsTotal} Wallets · ${positionRows.length} ${coin}-Positions gefunden`
        : `Top-50-Wallets aus dem Leaderboard. „Scan starten" lädt clearinghouseState pro Wallet.`;

  const subTabs: ReadonlyArray<SubTabSpec> = [
    { id: 'markets', label: 'Markets', icon: BarChart3 },
    { id: 'positions', label: 'Positionen', icon: ListChecks },
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { id: 'wallets', label: 'Wallet Tracker', icon: Eye },
  ];

  const walletSummaries = useMemo<WatchedWalletSummary[]>(
    () => wallets.map((w) => toSummary(w)),
    [wallets],
  );

  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);
  useEffect(() => {
    // Auto-select first wallet on load (oder wenn aktuell-selected nicht mehr in der Liste).
    const exists =
      selectedWalletId !== null && walletSummaries.some((w) => w.id === selectedWalletId);
    if (!exists && walletSummaries[0]) {
      setSelectedWalletId(walletSummaries[0].id);
    }
  }, [walletSummaries, selectedWalletId]);

  const selectedWallet =
    walletSummaries.find((w) => w.id === selectedWalletId) ?? null;
  const { stats, positions, loading } = useWalletDetail(selectedWallet, livePrices);

  // Lookup: address → LeaderboardRow für die Performance-Stats des selected
  // Wallets. lowercase-Key weil HL Adressen lowercase liefert, getrackte
  // Wallets aber gemischt sein können.
  const leaderboardByAddress = useMemo(() => {
    const m = new Map<string, (typeof lbRawRows)[number]>();
    for (const row of lbRawRows) {
      m.set(row.ethAddress.toLowerCase(), row);
    }
    return m;
  }, [lbRawRows]);
  const selectedLbRow = selectedWallet
    ? (leaderboardByAddress.get(selectedWallet.address.toLowerCase()) ?? null)
    : null;
  const selectedEveScore = selectedLbRow ? eveScore(selectedLbRow, 'allTime') : null;

  return (
    <div className="flex h-full flex-col">
      <HyperliquidMetricsBar
        selected={coin}
        onSelect={setCoin}
        metrics={currentMetrics}
        isConnected={isConnected}
        volume24hUsd={currentVolume ?? undefined}
      />

      <div className="flex shrink-0 gap-1.5 border-b border-white/5 px-4 py-2">
        {subTabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={
                active
                  ? 'flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/20 px-3 py-1.5 text-[13px] font-medium text-amber-300'
                  : 'flex items-center gap-1.5 rounded-lg border border-transparent px-3 py-1.5 text-[13px] font-medium text-neutral-500 hover:text-neutral-200'
              }
            >
              <Icon className="size-3.5" />
              {tab.label}
              {tab.badge && tab.badge > 0 ? (
                <span className="ml-1 rounded bg-rose-500/15 px-1 text-[10px] text-rose-300">
                  {tab.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="scroll-hidden flex-1 overflow-y-auto p-4">
        {activeTab === 'markets' ? (
          <MarketsTab coin={coin} hlFunding={currentMetrics?.funding ?? null} />
        ) : null}
        {activeTab === 'positions' ? (
          <PositionsTab
            coin={coin}
            rows={positionRows}
            totalWallets={positionsTotal || lbRawRows.length}
            loading={positionsLoading}
            subtitle={positionsScanSubtitle}
            onScan={() => void triggerPositionScan()}
          />
        ) : null}
        {activeTab === 'leaderboard' ? (
          <LeaderboardTab
            rows={leaderboardRows}
            totalCount={lbRawRows.length}
            trackedAddresses={wallets.map((w) => w.address)}
            onAddToTracker={async (input) => {
              const created = await addWallet({
                address: input.address,
                label: input.label,
              });
              setSelectedWalletId(String(created.id));
            }}
          />
        ) : null}
        {activeTab === 'wallets' ? (
          <WalletTrackerTab
            wallets={walletSummaries}
            selectedId={selectedWalletId}
            onSelect={setSelectedWalletId}
            alarms={{
              enabled: walletAlarms.enabled,
              supported: walletAlarms.supported,
              permission: walletAlarms.permission,
              toggle: walletAlarms.toggle,
            }}
            onAdd={async (input: AddWalletInput) => {
              const created = await addWallet(input);
              setSelectedWalletId(String(created.id));
            }}
            onDelete={async (id: string) => {
              await removeWallet(id);
              if (selectedWalletId === id) setSelectedWalletId(null);
            }}
            onUpdate={async (id: string, patch: UpdateWalletInput) => {
              await updateWallet(id, patch);
            }}
            stats={stats}
            positions={positions}
            positionsLoading={loading}
            leaderboardPerf={
              selectedLbRow
                ? {
                    accountValueUsd: selectedLbRow.accountValue,
                    day: selectedLbRow.perf.day,
                    week: selectedLbRow.perf.week,
                    month: selectedLbRow.perf.month,
                    allTime: selectedLbRow.perf.allTime,
                    eveScore: selectedEveScore,
                  }
                : null
            }
          />
        ) : null}
      </div>
    </div>
  );
}

// ── Wallet → Summary (Display-Shape für die linke Liste) ─────────────────────

function toSummary(wallet: WatchedWallet): WatchedWalletSummary {
  return {
    id: String(wallet.id),
    address: wallet.address,
    label: wallet.label?.trim() || wallet.address.slice(0, 6),
    thresholdUsd: wallet.threshold_usd ?? 1000,
    unreadCount: 0,
    wsLive: true,
  };
}

/** 2-char Initials für Avatar-Kreis im Leaderboard. */
function deriveInitials(displayName: string | undefined, address: string): string {
  if (displayName && displayName.trim().length > 0) {
    const parts = displayName.trim().split(/\s+/);
    const a = parts[0]?.[0] ?? '';
    const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
    const out = `${a}${b}`.toUpperCase();
    if (out.length > 0) return out;
  }
  // Fallback: erste 2 chars der Adresse nach "0x"
  return address.startsWith('0x') ? address.slice(2, 4).toUpperCase() : address.slice(0, 2).toUpperCase();
}

// ── Per-Wallet Stats + Positions on-select ───────────────────────────────────
//
// Fetch-Strategie:
//   - HTTP-Call beim Wallet-Wechsel (+ 30s refresh-interval).
//   - livePrices triggert KEINEN re-fetch — sondern nur ein useMemo das
//     Mark-Preis + uPnL pro Position neu rechnet. Sonst hätten wir jede
//     Sekunde einen 200ms-HTTP-Flackerer (WS-Mids ticken im 1s-Throttle).

const WALLET_DETAIL_REFRESH_MS = 30_000;

function useWalletDetail(
  wallet: WatchedWalletSummary | null,
  livePrices: Record<string, string>,
): {
  stats: WalletAccountStats | null;
  positions: WalletPositionRow[];
  loading: boolean;
} {
  const [stats, setStats] = useState<WalletAccountStats | null>(null);
  const [rawPositions, setRawPositions] = useState<HLPosition[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!wallet) {
      setStats(null);
      setRawPositions([]);
      return;
    }
    let cancelled = false;

    const load = async (isInitial: boolean): Promise<void> => {
      // Beim Initial-Load loading=true; bei Refresh-Tick KEIN Loading-Flash
      // damit positions sichtbar bleiben während neue Daten kommen.
      if (isInitial) setLoading(true);
      try {
        const state = await fetchClearinghouseState(wallet.address);
        if (cancelled) return;
        if (!state) {
          if (isInitial) {
            setStats(null);
            setRawPositions([]);
          }
          return;
        }
        const summary = state.marginSummary;
        const account = parseNum(summary?.accountValue);
        const totalMargin = parseNum(summary?.totalMarginUsed);
        const withdrawable = parseNum(state.withdrawable);
        const positions = parseOpenPositions(state);
        const unrealized = positions.reduce(
          (sum, p) => sum + parseNum(p.unrealizedPnl),
          0,
        );

        setStats({
          accountValueUsd: account,
          marginUsedUsd: totalMargin,
          freeMarginUsd: withdrawable,
          unrealizedPnlUsd: unrealized,
          unrealizedPnlPct: account > 0 ? (unrealized / account) * 100 : 0,
          marginUtilizationPct: account > 0 ? (totalMargin / account) * 100 : 0,
          freeMargin24hDeltaUsd: 0,
        });
        setRawPositions(positions);
      } catch (e) {
        if (cancelled) return;
        console.warn('[hyperliquid] fetchClearinghouseState failed:', e);
        // Beim Refresh-Tick keinen reset machen — letzte gute Daten beibehalten.
      } finally {
        if (!cancelled && isInitial) setLoading(false);
      }
    };

    void load(true);
    const id = setInterval(() => void load(false), WALLET_DETAIL_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [wallet]);

  // Mark + uPnL live aus livePrices ableiten — kein HTTP-Re-Fetch nötig.
  const positions = useMemo<WalletPositionRow[]>(
    () => rawPositions.map((p, i) => toPositionRow(p, i, livePrices)),
    [rawPositions, livePrices],
  );

  return { stats, positions, loading };
}

function toPositionRow(
  pos: HLPosition,
  index: number,
  livePrices: Record<string, string>,
): WalletPositionRow {
  const size = parseNum(pos.szi);
  const entry = parseNum(pos.entryPx);

  // mark beim letzten Fetch (HL liefert es nicht direkt im position-Obj,
  // wir fallen auf entry zurück — Live-Interpolation via livePrices).
  const markAtFetch = entry;
  const livePx = parseNum(livePrices[pos.coin]);
  const mark = livePx > 0 ? livePx : markAtFetch;

  // uPnL: HL liefert exakten unrealizedPnl inkl. Funding-Akkumulation.
  // Zwischen den 30s-Refreshes interpolieren wir nur die Mark-Delta.
  const upnlAtFetch = parseNum(pos.unrealizedPnl);
  const markDelta = mark - markAtFetch;
  const liveUpnl = upnlAtFetch + markDelta * size;

  const valueUsd = parseNum(pos.positionValue) || Math.abs(size) * mark;

  // Leverage: pos.leverage = { type: 'cross'|'isolated', value: number }
  const levRaw = pos.leverage as { type?: string; value?: unknown } | undefined;
  const leverage =
    typeof levRaw?.value === 'number'
      ? levRaw.value
      : parseNum(levRaw?.value as unknown);
  const leverageType: 'cross' | 'isolated' =
    levRaw?.type === 'isolated' ? 'isolated' : 'cross';

  // cumFunding: HL liefert { allTime, sinceOpen, sinceChange } als Strings.
  // sinceOpen = Funding seit Position-Open. Negativ = Wallet hat gezahlt.
  // HL-Konvention: positive Werte heißen "Wallet hat empfangen". UI dreht
  // das Vorzeichen nicht — wir zeigen den HL-Rohwert.
  const cumFundingRaw = pos.cumFunding as { sinceOpen?: unknown } | undefined;
  // HL liefert sinceOpen positiv wenn Funding GEZAHLT wurde (counterintuitiv).
  // Wir zeigen es als "Funding paid" → Negation: positive = profitabel (received).
  const fundingPaidUsd = -parseNum(cumFundingRaw?.sinceOpen);

  // Break-Even: entry plus accumulated funding pro Coin-Einheit.
  // BE = entry - (-fundingPaid / size) = entry + fundingPaid / size
  // (Anschaulich: wenn Wallet $7,526 funding gezahlt hat (fundingPaid=-7526),
  // muss der Preis $7526/size höher sein um break-even zu erreichen.)
  const fundingPerCoin = Math.abs(size) > 0 ? -fundingPaidUsd / Math.abs(size) : 0;
  // Für LONG: BE = entry + fundingPerCoin (price muss steigen)
  // Für SHORT: BE = entry - fundingPerCoin (price muss fallen)
  const beUsd = size >= 0 ? entry + fundingPerCoin : entry - fundingPerCoin;

  const liquidationPx = pos.liquidationPx ? parseNum(pos.liquidationPx) : null;

  return {
    id: `${pos.coin}-${index}`,
    coin: pos.coin,
    side: size >= 0 ? 'LONG' : 'SHORT',
    size: Math.abs(size),
    entry,
    beUsd,
    mark,
    valueUsd,
    upnlUsd: liveUpnl,
    leverage,
    leverageType,
    fundingPaidUsd,
    liquidationPx: liquidationPx && liquidationPx > 0 ? liquidationPx : null,
  };
}

function parseNum(v: unknown): number {
  if (v === undefined || v === null) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v !== 'string') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
